const pad = (value) => String(value).padStart(2, '0');

const getDateParts = (dateString) => {
    const match = String(dateString || '').match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);

    const date = new Date(Date.UTC(year, month - 1, day));

    if (
        date.getUTCFullYear() !== year ||
        date.getUTCMonth() !== month - 1 ||
        date.getUTCDate() !== day
    ) {
        return null;
    }

    return { year, month, day, date };
};

const formatUTCDate = (date) =>
    `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;

export const getPeriodKey = (dateString, groupBy = 'month') => {
    const parts = getDateParts(dateString);

    if (!parts) return null;

    if (groupBy === 'month') {
        return `${parts.year}-${pad(parts.month)}`;
    }

    const monday = new Date(parts.date);
    const weekday = (monday.getUTCDay() + 6) % 7;

    monday.setUTCDate(monday.getUTCDate() - weekday);

    return formatUTCDate(monday);
};

export const buildPeriods = (fromDate, toDate, groupBy) => {
    const start = getDateParts(fromDate);
    const end = getDateParts(toDate);

    if (!start || !end || start.date > end.date) return [];

    const periods = [];
    const current = new Date(start.date);

    if (groupBy === 'month') {
        current.setUTCDate(1);
    } else {
        current.setUTCDate(current.getUTCDate() - ((current.getUTCDay() + 6) % 7));
    }

    while (current <= end.date) {
        periods.push(
            groupBy === 'month'
                ? `${current.getUTCFullYear()}-${pad(current.getUTCMonth() + 1)}`
                : formatUTCDate(current)
        );

        if (groupBy === 'month') {
            current.setUTCMonth(current.getUTCMonth() + 1);
        } else {
            current.setUTCDate(current.getUTCDate() + 7);
        }
    }

    return periods;
};

export const aggregateSales = ({
    orders = [],
    selectedItemIds = [],
    fromDate,
    toDate,
    groupBy = 'month',
    metric = 'quantity',
}) => {
    const selectedIds = selectedItemIds.map(String);
    const selectedSet = new Set(selectedIds);

    const periods = buildPeriods(fromDate, toDate, groupBy);

    const grouped = Object.fromEntries(
        periods.map((period) => [
            period,
            {
                period,
                ...Object.fromEntries(selectedIds.map((id) => [id, 0])),
            },
        ])
    );

    orders.forEach((order) => {
        if (order.status !== 'paid') return;

        const orderDate = String(order.created_at || '').slice(0, 10);

        if (orderDate < fromDate || orderDate > toDate) return;

        const period = getPeriodKey(order.created_at, groupBy);

        if (!period || !grouped[period]) return;

        (order.items || []).forEach((orderItem) => {
            const productId = String(orderItem.item);

            if (!selectedSet.has(productId)) return;

            const value =
                metric === 'quantity'
                    ? Number(orderItem.quantity || 0)
                    : Number(
                          orderItem.total_price ??
                              Number(orderItem.item_price || 0) * Number(orderItem.quantity || 0)
                      );
            if (!Number.isFinite(value)) return;

            grouped[period][productId] += value;
        });
    });

    return periods.map((period) => grouped[period]);
};

export const getProductName = (product) =>
    product?.name || product?.title || `Product #${product?.id}`;
